<template>
    <div class="task-detail-container" data-testid="task-detail">
        <div
            class="task-content"
            :data-testid="detailBlocking || loading || errorVariant ? null : 'task-detail__main-content'"
        >
            <div
                class="task-detail-header-card d-flex flex-column"
                data-testid="task-detail__header-card"
            >
                <TaskDetailNavs
                    :current-task-id="taskId"
                    :has-prev="hasPrevTask"
                    :has-next="hasNextTask"
                    :peek-tasks="peekTasks"
                    :peek-page="peekPage"
                    :peek-total-pages="peekTotalPages"
                    :peek-total-task-count="peekTotalTaskCount"
                    :refresh-loading="detailBlocking || loading || refreshing"
                    data-testid="task-detail__navs"
                    @back="navigateBack"
                    @refresh="handleRefresh"
                    @prev-task="navigateAdjacentTask('prev')"
                    @next-task="navigateAdjacentTask('next')"
                    @select-task="navigateToTask"
                />

                <template v-if="!detailBlocking && !loading && !errorVariant">
                    <TaskDetailHeader
                        v-if="task && Object.keys(task).length > 0"
                        :key="taskHeaderKey"
                        :class="{ 'task-detail-header-card--refreshing': refreshing }"
                        :task="task"
                        :capabilities="taskCapabilities"
                        :threshold="task && typeof task.threshold === 'number' ? task.threshold : (Number(task.threshold) || 0)"
                        data-testid="task-detail__header"
                        @open-upload-drawer="sendReviewDra"
                        @change-status="changeChecklistStatus"
                        @refresh="handleRefresh"
                        @edit="openEditDialog"
                        @delete="handleDeleteTask"
                        @link-noa="openLinkBotDrawer"
                        @unlink-noa="handleUnlinkBot"
                    />

                    <TaskFinancialMetrics
                        v-if="task && Object.keys(task).length > 0"
                        :class="{ 'task-detail-header-card--refreshing': refreshing }"
                        :task="task"
                        :key="taskContentKey"
                        data-testid="task-detail__financial-metrics"
                    />

                    <TaskDetailsTabbed
                        v-if="task && Object.keys(task).length > 0"
                        ref="taskDetailsTabbed"
                        integrated
                        :class="{ 'task-detail-header-card--refreshing': refreshing }"
                        :task="task"
                        :file-names-list="fileNamesList"
                        :capabilities="taskCapabilities"
                        :initial-tab="taskDetailTab"
                        :dependencies-error="dependencyLoadFailed"
                        @tab-change="taskDetailTab = $event"
                        @refresh="handleRefresh"
                        @retry-dependencies="retryLoadTask"
                        @open-upload="sendReviewDra('files-section')"
                        data-testid="task-detail__tabbed"
                    />
                </template>
            </div>

            <TaskDetailSkeleton v-if="detailBlocking || loading" />
            <TaskDetailEmptyState
                v-else-if="errorVariant"
                :variant="errorVariant"
                @back="navigateBack"
                @retry="retryLoadTask"
            />

            <template v-if="!loading && !errorVariant">
            <TaskUploadModal
                v-model="sendforreviewdrawer"
                :modelValue="sendforreviewdrawer"
                @update:modelValue="sendforreviewdrawer = $event"
                :bulkworkpaper="bulkworkpaper"
                :fileuploaded="fileuploaded"
                :resetFlag="resetFlag"
                :fileremark="fileremark"
                :uploadiwithoutfile="uploadiwithoutfile"
                :viewtaskdata="task"
                :fileNamesList="fileNamesList"
                :id="projectId"
                :filesOnly="false"
                :auto-submit-on-upload="false"
                :numberOfEvidences="numberOfEvidences"
                :pageType="pageType"
                :redesign="true"
                @reset="handleTaskUploadModalReset"
                @remark-updated="handleRemarkUpdated"
                :task="task"
                @refresh="handleRefresh"
                @force-refresh="handleRefresh"
                data-testid="task-detail__upload-modal"
            />

            <AddTaskDialog
                ref="editTaskDialog"
                :visible.sync="editDialogVisible"
                :project-id="projectId"
                :custom-columns="customColumns"
                :category-acc="categoryAcc"
                :subcategory-acc="subcategoryAcc"
                :coa-category-acc="coaCategoryAcc"
                :coa-subcategory-acc="coaSubcategoryAcc"
                :location-list="locationList"
                :users="activeUsers"
                :due-date-min-date="dueDateMinDate"
                :due-date-max-date="dueDateMaxDate"
                :location-header="$store.state.LocationHeader || 'Location'"
                :all-tags="allTags"
                :possible-assertions="possibleAssertions"
                :loading="editDialogLoading"
                data-testid="task-detail__edit-dialog"
                @save="onEditSave"
            />

            <LinkBotDrawer
                :visible.sync="linkBotVisible"
                :bot-name="$constants.BOT_NAME"
                :selected-bot.sync="selectedBot"
                :selected-transaction-matching.sync="selectedTransactionMatching"
                :lease-schedule-value.sync="leaseScheduleValue"
                :bot-list="botList"
                :lease-schedule-items="leaseScheduleItems"
                :transaction-matching-list="transactionMatchingList"
                :loading="linkBotLoading"
                data-testid="task-detail__link-bot-drawer"
                @link="onLinkBotSubmit"
            />
            </template>
        </div>
    </div>
</template>

<script>
import TaskDetailNavs from './components/TaskDetailNavs/TaskDetailNavs.vue';
import TaskDetailHeader from './components/header/TaskDetailHeader.vue';
import TaskFinancialMetrics from './components/header/TaskFinancialMetrics.vue';
import TaskDetailsTabbed from './TaskDetailsTabbed.vue';
import TaskUploadModal from './modals/TaskUploadModal.vue';
import TaskDetailEmptyState from './components/TaskDetailEmptyState/TaskDetailEmptyState.vue';
import TaskDetailSkeleton from './components/TaskDetailSkeleton/TaskDetailSkeleton.vue';
import AddTaskDialog from '../TaskList/drawers/AddTaskDialog/AddTaskDialog.vue';
import LinkBotDrawer from '../TaskList/drawers/LinkBotDrawer/LinkBotDrawer.vue';
import { calculateUnexplainedDifference, calculateExplainedDifference, getCustomColumns } from '../../../utils/utils';
import { HORIZON_VIEWS } from '../../../utils/horizonConstants';
import { MAX_DUE_DATE_DAYS, TASK_DETAIL_ERRORS, TASK_DETAIL_TAB } from '../../../utils/constants';
import { getCycleDueDateWindow } from '../../../utils/workingDays';
import { resolveTaskActionCapabilities } from '../../../utils/taskDetailCapabilities';
import {
    getDetailNavigationTaskIds,
    getDetailNavigationIndex,
    getItemsPerPageFromState,
    getCurrentPageFromState,
    getQuickPeekTasks,
    getPageBoundaryNavigation,
} from '../../../utils/taskDetailNavigation';
import { getCachedTaskForDetail as resolveCachedTaskForDetail } from '../../../utils/tasklistUtils';
import { taskHasDependencyDetail, taskIdString } from '../../../utils/taskDependencyDetail';

export default {
    name: 'TaskDetail',
    components: {
        TaskDetailNavs,
        TaskDetailHeader,
        TaskFinancialMetrics,
        TaskDetailsTabbed,
        TaskUploadModal,
        TaskDetailEmptyState,
        TaskDetailSkeleton,
        AddTaskDialog,
        LinkBotDrawer,
    },
    data() {
        const projectId = this.$route.params.projectId;
        const taskId = this.$route.params.taskId;
        const userType = this.$route.query.userType || 'manager';
        const currentTask = this.$store.getters['tasks/getCurrentTask'] || {};
        const cachedTask = resolveCachedTaskForDetail(this.$store, projectId, taskId);

        return {
            projectId,
            taskId,
            task: cachedTask ? { ...cachedTask } : {},
            loading: !cachedTask,
            detailBlocking: false,
            refreshing: false,
            error: null,
            errorVariant: null,
            dependencyLoadFailed: false,
            addeditdata: cachedTask ? { ...cachedTask } : { ...currentTask },
            sendforreviewdrawer: false,
            bulkworkpaper: false,
            fileuploaded: [],
            resetFlag: false,
            fileremark: '',
            uploadiwithoutfile: false,
            fileNamesList: Array.isArray(cachedTask?.upload_v2) ? [...cachedTask.upload_v2] : [],
            taskDetailTab: '',
            userType,
            numberOfEvidences: 0,
            pageType: '',
            editDialogVisible: false,
            editDialogLoading: false,
            linkBotVisible: false,
            linkBotLoading: false,
            selectedBot: '',
            botSelectedId: '',
            selectedTransactionMatching: '',
            leaseScheduleValue: [],
        };
    },
    async created() {
        await this.loadTaskDetail();
    },
    computed: {
        storeError() {
            return this.$store.getters['tasks/getError'];
        },
        storeTask() {
            return this.$store.getters['tasks/getCurrentTask'];
        },
        currentStoreTask() {
            return this.storeTask;
        },
        taskListData() {
            return this.$store.getters['tasks/getTaskListData'] || {};
        },
        listViewState() {
            return this.$store.getters['tasks/getListViewState']?.(this.projectId) || {};
        },
        clientId() {
            return this.$store.state.client;
        },
        taskContentKey() {
            const id = this.task?._id || this.taskId;
            const updatedAt = this.task?.updated_at ?? '';
            const botId = this.task?.botid || this.task?.bot_details?._id || '';
            const schedule = this.task?.bot_recon_schedule_amount ?? '';
            const explained = this.task?.bot_recon_explained_difference ?? '';
            return `${id}-${updatedAt}-${botId}-${schedule}-${explained}`;
        },
        taskHeaderKey() {
            const caps = this.task?.capabilities || {};
            return [
                this.task?._id || this.taskId,
                this.task?.stage,
                this.task?.display_status_label,
                this.task?.workflow_phase,
                caps.can_submit,
                caps.can_l1_approve,
                caps.can_sign_off,
                caps.can_reject,
                caps.can_return_to_pending,
            ].join('|');
        },
        taskCapabilities() {
            return resolveTaskActionCapabilities({
                task: this.task,
                taskListData: this.taskListData,
                userId: this.$store.state.user?.id,
            });
        },
        navigationTaskIds() {
            return getDetailNavigationTaskIds(this.listViewState);
        },
        navigationIndex() {
            return getDetailNavigationIndex(this.listViewState, this.taskId);
        },
        hasPrevTask() {
            return this.navigationIndex > 0;
        },
        hasNextTask() {
            const ids = this.navigationTaskIds;
            return this.navigationIndex >= 0 && this.navigationIndex < ids.length - 1;
        },
        peekTasks() {
            const tasksById = {};
            (this.taskListData.checklist || []).forEach((t) => {
                if (t?._id) tasksById[t._id] = t;
            });
            return getQuickPeekTasks({
                taskIds: this.navigationTaskIds,
                tasksById,
                page: getCurrentPageFromState(this.listViewState),
                itemsPerPage: getItemsPerPageFromState(this.listViewState),
                currentTaskId: this.taskId,
            });
        },
        peekPage() {
            return getCurrentPageFromState(this.listViewState);
        },
        peekTotalPages() {
            const itemsPerPage = getItemsPerPageFromState(this.listViewState);
            const total = this.navigationTaskIds.length;
            return Math.max(1, Math.ceil(total / itemsPerPage));
        },
        peekTotalTaskCount() {
            return this.navigationTaskIds.length;
        },
        customColumns() {
            const schema = this.taskListData?.customColumnSchema || [];
            return getCustomColumns({}, schema);
        },
        categoryAcc() {
            const fromList = this.taskListData?.categoryAcc;
            if (Array.isArray(fromList) && fromList.length) return fromList;
            return this.$store.getters['tasks/getFormDataV3']?.categoryAcc || [];
        },
        subcategoryAcc() {
            const fromList = this.taskListData?.subcategoryAcc;
            if (Array.isArray(fromList) && fromList.length) return fromList;
            return this.$store.getters['tasks/getFormDataV3']?.subcategoryAcc || [];
        },
        coaCategoryAcc() {
            const fromList = this.taskListData?.coaCategoryAcc;
            if (Array.isArray(fromList) && fromList.length) return fromList;
            return this.$store.getters['tasks/getFormDataV3']?.coaCategoryAcc || [];
        },
        coaSubcategoryAcc() {
            const fromList = this.taskListData?.coaSubcategoryAcc;
            if (Array.isArray(fromList) && fromList.length) return fromList;
            return this.$store.getters['tasks/getFormDataV3']?.coaSubcategoryAcc || [];
        },
        locationList() {
            const filteredCycleId = this.$store.getters['tasks/getFilteredLocationsCycleId'];
            if (filteredCycleId && String(filteredCycleId) === String(this.projectId)) {
                return this.$store.getters['tasks/getFilteredLocations'] || [];
            }
            return this.$store.getters['tasks/getEnabledLocations'] || [];
        },
        activeUsers() {
            return this.$store.getters['tasks/getActiveUsersList'] || this.taskListData?.usersList || [];
        },
        allTags() {
            const masterDefaults = ['Yearly', 'Half-yearly', 'Monthly'];
            const fromChecklist = (this.taskListData.checklist || []).flatMap((t) => t.tags || []);
            return [...new Set([...masterDefaults, ...fromChecklist].filter(Boolean))];
        },
        possibleAssertions() {
            const fromList = this.taskListData?.possibleAssertions;
            if (Array.isArray(fromList) && fromList.length) return fromList;
            return this.$constants.TASK_LIST_CONSTANTS.ASSERTIONS || [];
        },
        dueDateMinDate() {
            return this.dueDateWindow.minIso;
        },
        dueDateMaxDate() {
            return this.dueDateWindow.maxIso;
        },
        dueDateWindow() {
            return getCycleDueDateWindow(this.taskListData?.metadata?.to, MAX_DUE_DATE_DAYS);
        },
        botList() {
            const fromStore = this.taskListData?.botList;
            if (Array.isArray(fromStore) && fromStore.length) {
                return fromStore;
            }
            return this.$constants.TASK_LIST_CONSTANTS?.BOT_LIST || [];
        },
        leaseScheduleItems() {
            const fromStore = this.taskListData?.leaseScheduleItems;
            if (Array.isArray(fromStore) && fromStore.length) {
                return fromStore;
            }
            return this.$constants.TASK_LIST_CONSTANTS?.LEASE_SCHEDULE_ITEMS || [];
        },
        transactionMatchingList() {
            return this.$store.getters['tasks/getTransactionMatchingList'] || [];
        },
    },
    watch: {
        selectedBot(botName) {
            this.botSelectedId = '';
            if (!botName) return;
            const name = Array.isArray(botName) ? botName[0] : botName;
            this.axios
                .post('/v2/bot/analysis/getbotidbyname', { data: [{ name }] })
                .then((dt) => {
                    if (dt.data.status === 'success') {
                        this.botSelectedId = dt.data.data[0]._id;
                    }
                })
                .catch((err) => {
                    console.log('[TaskDetail]: getbotidbyname error', err);
                });
        },
        storeError: {
            handler(error) {
                if (!error || this._taskDetailRequestInFlight) return;
                this.loading = false;
                if (error === TASK_DETAIL_ERRORS.NOT_FOUND) {
                    if (this.hasUsableCachedTask()) return;
                    this.setLoadError({ message: error });
                    return;
                }
                if (this.hasUsableCachedTask()) return;
                if (!this.errorVariant) {
                    this.setLoadError({ message: error });
                }
            },
        },
        '$route.params.taskId': {
            handler(newTaskId, oldTaskId) {
                if (newTaskId === oldTaskId) return;
                this.taskId = newTaskId;
                this.taskDetailTab = '';
                this.error = null;
                this.errorVariant = null;
                this.loadTaskDetail();
            },
        },
        '$route.params.projectId': {
            handler(newProjectId, oldProjectId) {
                if (newProjectId !== oldProjectId) {
                    this.projectId = newProjectId;
                }
            },
        },
        currentStoreTask(next) {
            if (this._taskDetailRequestInFlight || this.detailBlocking || this.loading) return;
            if (!next || taskIdString(next._id) !== taskIdString(this.taskId)) return;
            this.task = { ...next };
            this.fileNamesList = Array.isArray(next.upload_v2) ? [...next.upload_v2] : [];
        },
    },
    methods: {
        calculateUnexplainedDifference,
        calculateExplainedDifference,
        waitForPaint() {
            return new Promise((resolve) => {
                requestAnimationFrame(() => requestAnimationFrame(resolve));
            });
        },
        getCachedTaskForDetail(projectId, taskId) {
            return resolveCachedTaskForDetail(this.$store, projectId, taskId);
        },
        applyTaskPayload(task) {
            if (!task || taskIdString(task._id) !== taskIdString(this.taskId)) return;
            const hydrated = {
                ...task,
                canApprove: typeof task.canApprove === 'boolean'
                    ? task.canApprove
                    : task.blocked !== true,
            };
            this.$store.commit('tasks/SET_CURRENT_TASK', hydrated);
            this.task = { ...hydrated };
            this.addeditdata = { ...hydrated };
            this.fileNamesList = Array.isArray(hydrated.upload_v2)
                ? [...hydrated.upload_v2]
                : [];
        },
        async loadTaskDetail({ forceRefresh = false } = {}) {
            const requestedTaskId = this.taskId;
            const loadSeq = (this._taskDetailLoadSeq || 0) + 1;
            this._taskDetailLoadSeq = loadSeq;
            this._taskDetailInflightCount = (this._taskDetailInflightCount || 0) + 1;
            this._taskDetailRequestInFlight = true;
            this.error = null;
            this.errorVariant = null;
            this.dependencyLoadFailed = false;

            const isCurrentLoad = () => (
                this._taskDetailLoadSeq === loadSeq
                && taskIdString(this.taskId) === taskIdString(requestedTaskId)
            );

            const cold = !this.hasCycleMetadata(this.projectId);
            try {
                const cachedTask = this.getCachedTaskForDetail(this.projectId, requestedTaskId);
                if (cold || !cachedTask) {
                    if (!cachedTask) this.task = {};
                    this.loading = true;
                } else {
                    this.applyTaskPayload(cachedTask);
                    this.loading = false;
                }

                // Cold (refresh / no metadata for this cycle): getAllTasks posts
                // getdetails + getchecklist. Await getdetails before comments mount.
                // getchecklist and getFormData stay in flight after metadata._id is stored.
                // Warm (opened from the list): metadata._id is already in the store.
                let detailsPromise = Promise.resolve();
                if (cold) {
                    this.$store.dispatch('tasks/fetchFormDataV3').catch((err) => {
                        console.error('[TaskDetail]: fetchFormDataV3', err?.message || err);
                    });
                    this.$store.dispatch('tasks/getAllTasks', {
                        checklistId: this.projectId,
                    }).catch((err) => {
                        console.error('[TaskDetail]: getAllTasks', err?.message || err);
                    });
                    detailsPromise = this.$store.dispatch('tasks/waitForTasklistDetails', {
                        checklistId: this.projectId,
                    });
                }

                const [fetched] = await Promise.all([
                    this.$store.dispatch('tasks/fetchTask', {
                        checklistId: this.projectId,
                        taskId: requestedTaskId,
                        forceRefresh,
                    }),
                    detailsPromise,
                ]);
                if (!isCurrentLoad() || !fetched) return;
                this.dependencyLoadFailed = false;
                this.applyTaskPayload(fetched);
            } catch (err) {
                if (!isCurrentLoad()) return;
                console.log('[TaskDetail]: fetchTask', err?.message || err);
                if (this.hasUsableCachedTask() && !taskHasDependencyDetail(this.task)) {
                    this.dependencyLoadFailed = true;
                }
                if (!this.hasUsableCachedTask()) {
                    this.setLoadError(err);
                }
            } finally {
                this._taskDetailInflightCount = Math.max(0, (this._taskDetailInflightCount || 1) - 1);
                if (!isCurrentLoad() && taskIdString(this.task?._id) === taskIdString(this.taskId)) {
                    const storeId = taskIdString(this.$store.getters['tasks/getCurrentTask']?._id);
                    if (storeId !== taskIdString(this.taskId)) {
                        this.$store.commit('tasks/SET_CURRENT_TASK', this.task);
                    }
                }
                this._taskDetailRequestInFlight = this._taskDetailInflightCount > 0;
                if (isCurrentLoad()) {
                    this.loading = false;
                }
            }
        },
        sendReviewDra(source) {
            this.sendforreviewdrawer = true;
            this.pageType = source;
        },
        handleNumberOfEvidences(val) {
            this.numberOfEvidences = val;
        },
        handleTaskUpdated(newTask) {
            this.task = { ...newTask };
            this.$store.commit('tasks/SET_CURRENT_TASK', newTask);
        },
        syncAssignmentToStore(taskData) {
            const taskId = taskData?._id || this.taskId;
            if (!taskId) return;

            this.$store.commit('tasks/UPDATE_TASK', {
                checklistId: this.projectId,
                taskId,
                updates: {
                    preparer: taskData.preparer,
                    approver: taskData.approver,
                    sign_off_approvers: taskData.sign_off_approvers,
                    requires_sign_off: taskData.requires_sign_off,
                },
            });

            const updatedTask = this.$store.getters['tasks/getTaskById'](taskId);
            if (!updatedTask) return;

            this.$store.commit('tasks/SET_CURRENT_TASK', updatedTask);
            this.task = { ...updatedTask };
            this.addeditdata = { ...updatedTask };
        },
        openEditDialog() {
            this.$refs.editTaskDialog?.openForEdit(this.task);
        },
        async onEditSave({ addTaskData, customColumns, selectglsLocal, projectId }) {
            this.editDialogLoading = true;
            try {
                const result = await this.$store.dispatch('tasks/addEditChecklistItem', {
                    addeditdata: { ...addTaskData },
                    customColumns,
                    selectglsLocal,
                    projectId: projectId || this.projectId,
                });
                if (result?.success) {
                    this.$store.commit('sbSuccess', 'Task updated');
                    this.editDialogVisible = false;
                    await this.handleRefresh();
                    this.syncAssignmentToStore(addTaskData);
                } else if (result?.data?.index0) {
                    this.$refs.editTaskDialog?.setServerErrors(result.data.index0);
                } else {
                    this.$store.commit('sbError', result?.errorMessage || 'Failed to update task');
                }
            } catch (err) {
                this.$store.commit('sbError', err.message || err || 'Failed to update task');
            } finally {
                this.editDialogLoading = false;
            }
        },
        async handleDeleteTask() {
            const item = this.task;
            if (!item?._id) return;
            try {
                const result = await this.$store.dispatch('tasks/deleteChecklistItem', {
                    checklistId: this.projectId,
                    taskId: item._id,
                });
                if (result?.success) {
                    this.$store.commit('sbSuccess', 'Tasklist item deleted');
                    if (result.refreshError) {
                        this.$store.commit('sbError', result.refreshError);
                    }
                    this.navigateBack();
                } else {
                    this.$store.commit('sbError', result?.errorMessage || 'Unknown error');
                }
            } catch (err) {
                this.$store.commit('sbError', err.message || err || 'Unknown error!');
            }
        },
        async handleUnlinkBot() {
            try {
                const result = await this.$store.dispatch('tasks/unlinkBotTasks', {
                    checklistId: this.projectId,
                    taskIds: [this.task._id],
                });
                if (result.success) {
                    this.$store.commit('sbSuccess', `${this.$constants.BOT_NAME} Unlinked`);
                    await this.handleRefresh();
                }
            } catch (err) {
                this.$store.commit('sbError', err.message || 'Failed to unlink bot');
            }
        },
        openLinkBotDrawer() {
            this.selectedBot = this.task.bot_details?.name || '';
            this.botSelectedId = this.task.botid || '';
            this.leaseScheduleValue = [];
            this.selectedTransactionMatching = '';
            this.linkBotVisible = true;
            this.$store.dispatch('tasks/fetchTransactionMatchingAnalysis', { checklistId: this.projectId }).catch(() => {});
        },
        async onLinkBotSubmit() {
            if (!this.selectedBot || !this.task?._id) {
                this.$store.commit('sbError', `Please select an ${this.$constants.BOT_NAME} and task.`);
                return;
            }
            const link = {
                botid: this.botSelectedId,
                task_id: this.task._id,
                bot_result_tags: [],
            };
            if (this.selectedBot === 'Lease Schedule') {
                link.bot_result_tags = this.leaseScheduleValue;
            }
            if (this.selectedBot === 'Transaction Matching') {
                link.tmaid = this.selectedTransactionMatching;
            }
            this.linkBotLoading = true;
            try {
                const result = await this.$store.dispatch('tasks/linkBotTasks', {
                    checklistId: this.projectId,
                    links: [link],
                });
                if (result.success) {
                    this.$store.commit('sbSuccess', `${this.$constants.BOT_NAME} Linked`);
                    this.linkBotVisible = false;
                    this.selectedBot = '';
                    this.botSelectedId = '';
                    await this.handleRefresh();
                }
            } finally {
                this.linkBotLoading = false;
            }
        },
        navigateToTask(task) {
            if (!task?._id || task._id === this.taskId) return;
            const index = this.navigationTaskIds.indexOf(task._id);
            this.$store.dispatch('tasks/saveListViewState', {
                projectId: this.projectId,
                partial: {
                    detailNavigationIndex: index >= 0 ? index : this.navigationIndex,
                },
            });
            this.$router.push({
                path: `/financialclose/project/checklist/${this.projectId}/task/${task._id}`,
                query: { ...this.$route.query },
            });
        },
        navigateAdjacentTask(direction) {
            const nav = getPageBoundaryNavigation(this.listViewState, this.taskId, direction);
            if (!nav?.taskId) return;
            this.$store.dispatch('tasks/saveListViewState', {
                projectId: this.projectId,
                partial: {
                    detailNavigationIndex: nav.index,
                    detailNavigationPage: nav.page,
                },
            });
            this.$router.push({
                path: `/financialclose/project/checklist/${this.projectId}/task/${nav.taskId}`,
                query: { ...this.$route.query },
            });
        },
        handleRemarkUpdated(newRemark) {
            if (this.task) {
                this.task.remark = newRemark;
                this.task.remarkDate = new Date();
                this.updateTask({ remark: newRemark, remarkDate: this.task.remarkDate });
            }
        },
        async changeChecklistStatus({ id, status, remark, approver_remark }) {
            try {
                await this.$store.dispatch('tasks/changeChecklistStatus', {
                    checklistId: this.projectId,
                    taskId: id,
                    status,
                    remark: remark ?? approver_remark,
                });
                await this.handleRefresh();
            } catch (err) {
                console.log(err);
            }
        },
        handleTaskUploadModalReset() {
            this.fileuploaded = [];
            this.fileremark = '';
            this.uploadiwithoutfile = false;
            this.resetFlag = !this.resetFlag;
        },
        isActivityTabActive() {
            const activeTab = this.$refs.taskDetailsTabbed?.activeTab || this.taskDetailTab;
            return activeTab === TASK_DETAIL_TAB.ACTIVITY;
        },
        /**
         * Reload activity logs for this task into the store. Caller must already know the activity tab is active.
         */
        async fetchActivityLogForTask(taskId) {
            if (!taskId) return;
            try {
                await this.$store.dispatch('fetchActivityLog', {
                    resourceId: taskId,
                    type: [],
                    consolidated: false,
                });
            } catch (err) {
                console.log('[TaskDetail]:', err);
            }
        },
        /**
         * Reload this task after submit, stage change, refresh, or edit.
         * detailBlocking stays up for v3 getTask and v3 getdetails only.
         * getchecklist and getFormData run in the background.
         * Activity log, when that tab is open, does not hold detailBlocking.
         * Child @refresh payloads are ignored.
         * getTask returns bot_details for this task. getchecklist stays in the background.
         */
        async handleRefresh() {
            if (this.refreshing) return;
            this.refreshing = true;
            this.detailBlocking = true;
            await this.$nextTick();
            await this.waitForPaint();
            const taskId = this.task?._id || this.taskId;
            if (this.isActivityTabActive() && taskId) {
                this.fetchActivityLogForTask(taskId);
            }
            try {
                if (this.projectId) {
                    await this.$store.dispatch('tasks/refreshTaskAfterChange', {
                        checklistId: this.projectId,
                        taskId: this.taskId,
                    });
                    const updatedTask = this.$store.getters['tasks/getCurrentTask'];
                    if (updatedTask && taskIdString(updatedTask._id) === taskIdString(this.taskId)) {
                        this.task = { ...updatedTask };
                        this.fileNamesList = Array.isArray(updatedTask.upload_v2)
                            ? [...updatedTask.upload_v2]
                            : [];
                    }
                }
            } catch (err) {
                const message = err?.message || TASK_DETAIL_ERRORS.FETCH_FAILED;
                if (message === TASK_DETAIL_ERRORS.NOT_FOUND) {
                    this.setLoadError(err);
                    return;
                }
                console.log('[TaskDetail]:', err);
                this.$store.commit('sbError', message);
            } finally {
                this.detailBlocking = false;
                this.refreshing = false;
            }
        },
        updateTask(updatedData) {
            this.$store.dispatch('tasks/updateTask', {
                checklistId: this.projectId,
                taskId: this.taskId,
                updates: updatedData,
            })
                .then(() => this.$toast.success('Task updated successfully'))
                .catch((err) => {
                    console.error('Error updating task:', err);
                    this.$toast.error('Failed to update task');
                });
        },
        setLoadError(err) {
            const message = err?.message || TASK_DETAIL_ERRORS.FETCH_FAILED;
            this.error = message;
            this.errorVariant = message === TASK_DETAIL_ERRORS.NOT_FOUND
                ? 'not-found'
                : 'load-error';
        },
        hasUsableCachedTask() {
            return Boolean(this.task && this.task._id);
        },
        hasCycleMetadata(projectId) {
            const id = this.$store.getters['tasks/getTaskListData']?.metadata?._id;
            return id != null && String(id) === String(projectId);
        },
        async retryLoadTask() {
            this.error = null;
            this.errorVariant = null;
            await this.loadTaskDetail({ forceRefresh: true });
        },
        navigateBack() {
            const query = { cid: this.clientId };
            const tasklistView = this.$store.getters['tasks/getTasklistView'](this.projectId);
            if (tasklistView === HORIZON_VIEWS.HORIZON) {
                query.view = HORIZON_VIEWS.HORIZON;
            }
            if (this.$route.query.userType) {
                query.userType = this.$route.query.userType;
            }
            this.$router.replace({
                path: `/financialclose/project/checklist/${this.projectId}`,
                query,
            }).catch((err) => {
                if (err?.name !== 'NavigationDuplicated') {
                    console.error('[TaskDetail]: navigateBack router.replace failed', err);
                }
            });
        },
    },
};
</script>

<style scoped>
/* .task-detail-container {
    padding: var(--space-sm);
} */

.task-detail-header-card {
    background: var(--grey-white);
    padding: var(--space-sm);
    border-radius: var(--radius-sm, 8px);
    gap: var(--space-4);

    &--refreshing {
        opacity: 0.72;
        pointer-events: none;
    }
}

.task-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
}
</style>
